/*
 * The page's one cache of image object URLs, made available to everything below.
 *
 * Wrap the components that show an article's images - the editor and the
 * Images card - and each `useArticleImagePreviews` under it registers with the
 * same store: one download per image for the page, re-used wherever it appears,
 * released when nothing wants it, and everything released when this unmounts.
 *
 * Two layers. `ArticleImagePreviewsScope` takes the api as a prop and owns the
 * store; that is the piece a test renders with a fake. `ArticleImagePreviewsProvider`
 * is what the page uses - it reads the admin api from the session and hands it
 * down.
 *
 * Either layer takes `articleId` - the article shorthand - or an `imageOwner`,
 * which wins when both are given (S42, 2026-10-12): the release-note editor
 * wraps its editor and Images card in the same provider with its own owner, and
 * the store is keyed on that owner's namespaced key, so the consumers under it
 * find it by the same key (see imageOwner.ts).
 */

import type { ReactNode } from 'react';
import type { AdminApi } from '../../api/adminApi.js';
import { useAdminApi } from '../../auth/AuthProvider.js';
import {
  ArticleImagePreviewsContext,
  useOwnedArticleImagePreviewStore,
  useOwnedImagePreviewStore,
} from './useArticleImagePreviews.js';
import type { ImagePreviewSource } from './imageOwner.js';

export function ArticleImagePreviewsScope({
  api,
  articleId,
  imageOwner,
  children,
}: {
  api: AdminApi;
  articleId?: string | undefined;
  /** Whose images these are when they are not an article's. Wins over `articleId`. */
  imageOwner?: ImagePreviewSource | undefined;
  children: ReactNode;
}): JSX.Element {
  // Both hooks run on every render - hooks cannot be conditional - and each is
  // handed nothing when the other is the one in use, so only one store exists.
  const ownerStore = useOwnedImagePreviewStore(imageOwner ?? null);
  const articleStore = useOwnedArticleImagePreviewStore(api, imageOwner ? undefined : articleId);
  const store = imageOwner ? ownerStore : articleStore;
  return (
    <ArticleImagePreviewsContext.Provider value={store}>{children}</ArticleImagePreviewsContext.Provider>
  );
}

export function ArticleImagePreviewsProvider({
  articleId,
  imageOwner,
  children,
}: {
  articleId?: string | undefined;
  /** As on ArticleImagePreviewsScope: wins over `articleId`. */
  imageOwner?: ImagePreviewSource | undefined;
  children: ReactNode;
}): JSX.Element {
  const api = useAdminApi();
  return (
    <ArticleImagePreviewsScope api={api} articleId={articleId} imageOwner={imageOwner}>
      {children}
    </ArticleImagePreviewsScope>
  );
}
